import { useEffect, useState } from 'react'
import useFinishFlow, { FLOWS } from 'utilities/navigation/flow'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import Header from 'components/headers/withGreenBack'
import { createCashbox } from 'services/cashbox'
import {
    Card,
    DAYS_OF_MONTH,
    Field,
    MoneyInput,
    Notice,
    PrimaryButton,
    TextInput,
    WEEK_DAYS,
    apiMessage,
    inputClass,
    parseDigits,
} from 'modules/cashbox/ui'
import { faPlain, toman } from 'utilities/helper/format'
import FundGoalPicker, {
    FUND_GOAL_QUESTION,
    FUND_GOAL_REQUIRED,
    FundGoal,
} from 'components/fundGoal'

/**
 * ساخت صندوق قرعه‌کشی.
 *
 * Deliberately NOT the moneypool creation flow: a cashbox has no join link and
 * no self-service membership. The manager creates the box here and then adds
 * every member by phone number on the next screen — which is also how Android
 * works, and why nothing on this path exposes `membership/request/`.
 *
 * The box is created INACTIVE. It only starts running when the manager
 * activates it from the members screen, because activation is what fixes the
 * share count and opens the first cycle.
 */
export default function CreateCashboxPage() {
    // The box exists now: the chooser and this form are spent, and back from
    // the box must not offer to create it again.
    const finishFlow = useFinishFlow(FLOWS.cashboxCreate)
    const queryClient = useQueryClient()

    const [name, setName] = useState('')
    const [goal, setGoal] = useState<FundGoal | ''>('')
    const [goalDescription, setGoalDescription] = useState('')
    const [shareValue, setShareValue] = useState(0)
    const [interval, setIntervalValue] = useState<CashboxInterval>('m')
    const [dayOfDraw, setDayOfDraw] = useState(1)
    const [numberOfLoans, setNumberOfLoans] = useState(1)
    const [ownerShares, setOwnerShares] = useState(1)
    const [managerFirstWinner, setManagerFirstWinner] = useState(false)
    const [plan, setPlan] = useState<'cms' | 'sub'>('cms')

    // The head of the activation funnel. `cashbox_created` alone cannot tell a
    // form nobody submitted from a form that keeps 400ing, and this screen has
    // five client-side validations that can hold the button down indefinitely.
    useEffect(() => {
        posthog.capture('cashbox_create_started')
    }, [])

    const errors: { [key: string]: string } = {}
    if (name.trim().length < 3) errors.name = 'نام صندوق حداقل ۳ حرف باشد'
    if (goal === '') errors.goal = FUND_GOAL_REQUIRED
    if (shareValue <= 0) errors.shareValue = 'ارزش هر سهم را وارد کنید'
    if (numberOfLoans < 1) errors.numberOfLoans = 'حداقل یک وام در هر نوبت'
    if (ownerShares < 0) errors.ownerShares = 'تعداد سهم نمی‌تواند منفی باشد'
    // `create_cashbox` rejects this combination outright (400): a manager with
    // no share cannot be the first winner of a draw they are not in.
    if (ownerShares === 0 && managerFirstWinner)
        errors.managerFirstWinner =
            'مدیری که سهمی ندارد نمی‌تواند برنده‌ی نوبت اول باشد'

    const isValid = Object.keys(errors).length === 0

    const create = useMutation({
        mutationFn: () =>
            createCashbox({
                name: name.trim(),
                image_id: 1,
                number_of_shares: ownerShares,
                plan,
                goal,
                goal_description: goalDescription.trim(),
                period: {
                    share_value: shareValue,
                    interval,
                    day_of_draw: dayOfDraw,
                    number_of_loans: numberOfLoans,
                    is_manager_first_winner: managerFirstWinner,
                },
            }),
        onSuccess: (result) => {
            posthog.capture('cashbox_created', {
                cashbox_id: result?.cashbox?.id,
                share_value: shareValue,
                number_of_loans: numberOfLoans,
                plan,
                goal,
                has_goal_description: goalDescription.trim() !== '',
            })
            queryClient.invalidateQueries(['get-boxes'])
            // A subscription box is born in `unp` and cannot be activated
            // until an Order is paid, so send the manager to the plan screen
            // instead of the members screen they cannot finish from.
            if (result?.cashbox?.state === 'unp') {
                toast.success('صندوق ساخته شد؛ برای شروع، نسخه را انتخاب کنید')
                finishFlow(`/cashbox/${result.cashbox.id}/plans`)
                return
            }
            toast.success('صندوق ساخته شد؛ حالا اعضا را اضافه کنید')
            finishFlow(`/cashbox/${result.cashbox.id}/members`)
        },
        onError: (error: any) => {
            posthog.capture('cashbox_create_failed', {
                status: error?.response?.status,
                plan,
                share_value: shareValue,
                number_of_loans: numberOfLoans,
                goal,
            })
            toast.error(apiMessage(error))
        },
    })

    return (
        <div className="bg-white min-h-screen pb-8">
            <Header title="ساخت صندوق قرعه‌کشی" />

            <div className="px-4 pt-2">
                <div className="mt-4">
                    <Field label="نام صندوق" error={errors.name}>
                        <TextInput
                            value={name}
                            maxLength={100}
                            placeholder="مثلاً صندوق همکاران"
                            onChange={(event) => setName(event.target.value)}
                        />
                    </Field>

                    {/* Not a <Field>: that is a <label>, and a label around a
                        group of buttons forwards every tap on its text to the
                        first chip. */}
                    <div className="mb-4">
                        <p className="text-sm text-text-body mb-1.5">
                            {FUND_GOAL_QUESTION}
                        </p>
                        <FundGoalPicker
                            goal={goal}
                            description={goalDescription}
                            onGoalChange={setGoal}
                            onDescriptionChange={setGoalDescription}
                            boxKind="cashbox"
                            error={errors.goal}
                        />
                    </div>

                    <Field
                        label="ارزش هر سهم"
                        error={errors.shareValue}
                        hint="مبلغی که هر عضو در هر نوبت بابت یک سهم می‌پردازد"
                    >
                        <MoneyInput
                            value={shareValue}
                            onChange={setShareValue}
                            invalid={Boolean(errors.shareValue)}
                        />
                    </Field>

                    <Field label="دوره‌ی قرعه‌کشی">
                        <div className="flex gap-2">
                            {(
                                [
                                    { value: 'm', label: 'ماهانه' },
                                    { value: 'w', label: 'هفتگی' },
                                ] as Array<{
                                    value: CashboxInterval
                                    label: string
                                }>
                            ).map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => {
                                        setIntervalValue(option.value)
                                        setDayOfDraw(
                                            option.value === 'w' ? 6 : 1
                                        )
                                    }}
                                    className={`flex-1 h-11 rounded-[10px] border text-sm ${
                                        interval === option.value
                                            ? 'border-green-1 bg-[#E6F3DD] text-green-1 font-bold'
                                            : 'border-divider bg-white text-text-body'
                                    }`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </Field>

                    <Field
                        label={
                            interval === 'w'
                                ? 'روز قرعه‌کشی در هفته'
                                : 'روز قرعه‌کشی در ماه'
                        }
                    >
                        <select
                            value={dayOfDraw}
                            onChange={(event) =>
                                setDayOfDraw(Number(event.target.value))
                            }
                            className={inputClass}
                        >
                            {interval === 'w'
                                ? WEEK_DAYS.map((day) => (
                                      <option key={day.value} value={day.value}>
                                          {day.label}
                                      </option>
                                  ))
                                : DAYS_OF_MONTH.map((day) => (
                                      <option key={day} value={day}>
                                          {faPlain.format(day)}
                                      </option>
                                  ))}
                        </select>
                    </Field>

                    <Field
                        label="تعداد وام در هر نوبت"
                        error={errors.numberOfLoans}
                        hint="در هر قرعه‌کشی چند نفر برنده می‌شوند"
                    >
                        <TextInput
                            inputMode="numeric"
                            value={numberOfLoans || ''}
                            onChange={(event) =>
                                setNumberOfLoans(
                                    parseDigits(event.target.value)
                                )
                            }
                            invalid={Boolean(errors.numberOfLoans)}
                        />
                    </Field>

                    <Field
                        label="تعداد سهم شما"
                        error={errors.ownerShares}
                        hint="اگر خودتان در قرعه‌کشی شرکت نمی‌کنید، صفر بگذارید"
                    >
                        <TextInput
                            inputMode="numeric"
                            value={String(ownerShares)}
                            onChange={(event) =>
                                setOwnerShares(parseDigits(event.target.value))
                            }
                            invalid={Boolean(errors.ownerShares)}
                        />
                    </Field>

                    <Card className="mb-4">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={managerFirstWinner}
                                onChange={(event) =>
                                    setManagerFirstWinner(event.target.checked)
                                }
                                className="w-4 h-4 mt-0.5 accent-green-1"
                            />
                            <span>
                                <span className="block text-sm text-black">
                                    برنده‌ی نوبت اول، مدیر صندوق باشد
                                </span>
                                <span className="block text-xs text-text-secondary mt-0.5">
                                    نوبت اول بدون قرعه‌کشی به شما می‌رسد
                                </span>
                            </span>
                        </label>
                        {errors.managerFirstWinner && (
                            <p className="text-xs text-[#B3261E] mt-2">
                                {errors.managerFirstWinner}
                            </p>
                        )}
                    </Card>

                    <p className="text-sm font-bold text-black mb-2">
                        نسخه‌ی صندوق
                    </p>
                    <div className="mb-4">
                        {(
                            [
                                {
                                    value: 'cms',
                                    title: 'کارمزدی',
                                    body: 'بابت خودِ صندوق چیزی نمی‌پردازید؛ هنگام پرداخت آنلاین سهم، از هر عضو کارمزد گرفته می‌شود. صندوق بلافاصله قابل استفاده است.',
                                },
                                {
                                    value: 'sub',
                                    title: 'اشتراکی',
                                    body: 'مبلغی ماهانه بابت صندوق می‌پردازید و از اعضا کارمزد گرفته نمی‌شود. تا پرداخت اشتراک، صندوق فعال نمی‌شود.',
                                },
                            ] as Array<{
                                value: 'cms' | 'sub'
                                title: string
                                body: string
                            }>
                        ).map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => setPlan(option.value)}
                                className={`w-full text-right rounded-card border p-3 mb-2 ${
                                    plan === option.value
                                        ? 'border-green-1 bg-[#E6F3DD]'
                                        : 'border-divider bg-white'
                                }`}
                            >
                                <span
                                    className={`block text-sm font-bold ${
                                        plan === option.value
                                            ? 'text-green-1'
                                            : 'text-black'
                                    }`}
                                >
                                    {option.title}
                                </span>
                                <span className="block text-xs text-text-secondary mt-1 leading-5">
                                    {option.body}
                                </span>
                            </button>
                        ))}
                    </div>

                    {shareValue > 0 && numberOfLoans > 0 && (
                        <Notice tone="info">
                            با {faPlain.format(numberOfLoans)} وام در هر نوبت،
                            مبلغ هر وام برابر است با ارزش هر سهم × تعداد کل
                            سهم‌ها ÷ {faPlain.format(numberOfLoans)}. تعداد کل
                            سهم‌ها بعد از افزودن اعضا مشخص می‌شود و باید بر{' '}
                            {faPlain.format(numberOfLoans)} بخش‌پذیر باشد، وگرنه
                            صندوق فعال نمی‌شود.
                        </Notice>
                    )}

                    <div className="mt-5">
                        <PrimaryButton
                            disabled={!isValid || create.isLoading}
                            onClick={() => create.mutate()}
                        >
                            {create.isLoading
                                ? 'در حال ساخت…'
                                : 'ساخت صندوق و افزودن اعضا'}
                        </PrimaryButton>
                    </div>

                    {shareValue > 0 && (
                        <p className="text-xs text-text-secondary text-center mt-3">
                            هر عضو در هر نوبت {toman(shareValue)} بابت هر سهم
                            می‌پردازد
                        </p>
                    )}
                </div>
            </div>
        </div>
    )
}
